import { GetStoresResponseSuccess } from '@bounty/types'
import {
  H4,
  Image,
  isWeb,
  PageLoading,
  Paragraph,
  XStack,
  YStack,
  Link,
} from '@bounty/creators-design-system'
import { FlatList } from 'react-native'
import { isWithinLast30Days, prettyCurrency, prettyNumber } from '@bounty/utils'
import useInfiniteScroll from 'react-infinite-scroll-hook'
import { useCallback } from 'react'
import { useShopList } from '../useShopList'
import { cloudinary } from '../../../utils/cloudinary'
import { useShopListContext } from './ShopListContext'
import NewBriefTag from '../../../assets/new-brief-tag.png'
import { logger } from '@bounty/creators-common/utils/logger'

type ShopListItemProps = {
  shop: GetStoresResponseSuccess['data']['items'][0]
  variant?: 'regular' | 'featured'
  index: number
  native: boolean
}

/**
 * NOTE: keep this in sync with useShopList.tsx
 */
//const hardcodedFeaturedShops = ['nike', 'pantene', 'hair-food', 'bonnie-plants']
const hardcodedFeaturedShops = ['bonnie-plants']

const ShopListItem = ({
  shop,
  variant = 'regular',
  native,
}: ShopListItemProps) => {
  const adjustedImage = shop.slug === 'nike'

  return (
    <YStack
      width={variant === 'regular' && !native ? '50%' : '100%'}
      $gtMd={{ width: variant === 'regular' ? '25%' : '50%' }}
      p="$2"
    >
      <Link
        href={
          hardcodedFeaturedShops.includes(shop.slug)
            ? shop.shopUrl
            : `/shop/${shop.slug}`
        }
        event="Shop List Store Tile Clicked"
      >
        <YStack flexShrink={0}>
          <YStack
            height={200}
            $gtMd={{ height: 300 }}
            position="relative"
            overflow="hidden"
            borderRadius={'$2'}
          >
            {/*
          // @ts-expect-error - Tamagui is wrong here */}
            <Image
              borderRadius={'$3'}
              paddingTop={adjustedImage ? 600 : 0}
              $gtMd={{ paddingTop: adjustedImage ? 550 : 0 }}
              flex={1}
              resizeMode="cover"
              src={cloudinary({
                height: variant === 'regular' ? 300 : 500,
                mode: 'c_fit',
              })(shop.headerImgLinkPrimary!)}
            />
            <YStack
              zIndex={10}
              position="absolute"
              bottom={'$1'}
              left={'$1'}
              right={'$1'}
              backgroundColor="$neutral.50"
              borderRadius={'$2'}
            >
              <H4
                numberOfLines={1}
                size="$sm"
                px="$3"
                py="$1"
                $gtMd={{ size: '$md' }}
              >
                {shop.name}
              </H4>
            </YStack>
            {variant === 'featured' ? (
              <YStack
                zIndex={10}
                position="absolute"
                top={'$1'}
                right={'$1'}
                backgroundColor="$neutral.50"
                borderRadius={'$2'}
              >
                <H4 numberOfLines={1} size="$xs" px="$3" py="$1">
                  Featured
                </H4>
              </YStack>
            ) : (
              <YStack
                zIndex={10}
                position="absolute"
                top={'$1'}
                right={'$1'}
                backgroundColor="$neutral.50"
                borderRadius={'$2'}
              >
                <H4 numberOfLines={1} size="$xs" px="$3" py="$1">
                  {prettyCurrency(shop.minBountyPayment)} min
                </H4>
              </YStack>
            )}
          </YStack>
          <YStack>
            <Paragraph size="$sm" color="$neutral.500" $gtMd={{ size: '$md' }}>
              Earn up to
            </Paragraph>

            <Paragraph size="$sm" fontWeight={'$bold'} $gtMd={{ size: '$md' }}>
              {prettyCurrency(shop.startingCPM)} for{' '}
              {prettyNumber(shop.startingCPMViews)} views.
            </Paragraph>
          </YStack>
        </YStack>
      </Link>
    </YStack>
  )
}

export type ShopListProps = unknown

const ShopListWeb = () => {
  const { items, hasNextPage, setSize, isLoading, error } = useShopList()
  const [{ sortOrder }] = useShopListContext()

  // Cache setSize to avoid infinite loop of death!
  const onLoadMore = useCallback(() => {
    setSize((x) => x + 1)
  }, [setSize])

  const [sentryRef] = useInfiniteScroll({
    loading: isLoading,
    hasNextPage,
    onLoadMore: onLoadMore,
    // When there is an error, we stop infinite loading.
    // It can be reactivated by setting "error" state as undefined.
    disabled: !!error,
    // `rootMargin` is passed to `IntersectionObserver`.
    // We can use it to trigger 'onLoadMore' when the sentry comes near to become
    // visible, instead of becoming fully visible on the screen.
    rootMargin: '0px 0px 100px 0px',
  })

  if (!items || items.length === 0) return <PageLoading />

  return (
    <XStack flexWrap="wrap">
      {items.map((item, index) => {
        return (
          <ShopListItem
            key={item.id}
            index={index}
            variant={
              item.featured && sortOrder === 'recommended'
                ? 'featured'
                : 'regular'
            }
            shop={item}
            native={false}
          />
        )
      })}

      {(isLoading || hasNextPage) && (
        // @ts-expect-error - Works, the refs just aren't playing nice*
        <XStack ref={sentryRef} />
      )}
    </XStack>
  )
}

const ShopListNative = () => {
  const { items, setSize } = useShopList()
  const [{ sortOrder }] = useShopListContext()

  // Cache order infinite loop of death!
  const onLoadMore = useCallback(() => {
    setSize((x) => x + 1)
  }, [setSize])

  if (!items || items.length === 0) return <PageLoading />

  if (items.length === 1) {
    return (
      <ShopListItem
        index={0}
        variant={items[0].featured ? 'featured' : 'regular'}
        shop={items[0]}
        native={true}
      />
    )
  }

  return (
    <FlatList<typeof items[0]>
      data={items}
      numColumns={2}
      onEndReachedThreshold={0.95}
      onEndReached={() => {
        onLoadMore()
      }}
      renderItem={({ item, index }) => {
        return (
          <YStack flex={1}>
            <ShopListItem
              index={index}
              variant={
                item.featured && sortOrder === 'recommended'
                  ? 'featured'
                  : 'regular'
              }
              shop={item}
              native={true}
            />
          </YStack>
        )
      }}
    />
  )
}

export const ShopList = () => {
  return isWeb ? <ShopListWeb /> : <ShopListNative />
}
